/* global API, Site, Icons */

import { createApp } from 'vue';
import Paginate from 'vuejs-paginate-next';
import { LMap, LTileLayer, LMarker, LPopup, LControlZoom, LIcon } from '@vue-leaflet/vue-leaflet';
import 'leaflet/dist/leaflet.css';
import MarkerCluster from './plugins/MarkerCluster.vue';
import { useModalData, useOpenModal, useCloseModal } from './composables/modal';
import { usePaginationData, usePages, useChangePage, usePageStart, usePageEnd } from './composables/paging';
import {
	useQuery,
	useGetTitle,
	useGetDesc,
	useGetShortDesc,
	useHasShortDesc,
	useCreateParams,
	useGetParams,
} from './composables/data';
import { useOpenEmbed, useCloseEmbed, useEmbedData, useCreateEmbed, useCopyEmbed } from './composables/embed';
import ld from 'lodash';
let _ = ld.noConflict();

// Vue element
const vueSelector = '.vue-services';
const vueElement = document.querySelector(vueSelector);

if (vueElement) {
	const appOptions = {
		components: {
			LMap,
			LTileLayer,
			LMarker,
			LPopup,
			LControlZoom,
			LIcon,
			MarkerCluster,
			Paginate,
		},
		data() {
			return {
				lang: Site.lang_slug,
				fetchesLoading: {},
				results: {},
				allResults: {},
				format: 'map',
				tileLayer: {
					url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
					attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
				},
				config: {
					center: [65, 25.5],
					zoom: 4.5,
					maxZoom: 19,
				},
				mapOptions: {
					zoomControl: false,
				},
				modal: {
					show: false,
					title: '',
					description: '',
					href: '',
				},
				termCounts: {},
				serviceList: [],
				emptyQuery: _.cloneDeep(useQuery),
				showPostType: false,
				helpers: {
					loading: true,
				},
				useQuery,
				usePaginationData,
				useModalData,
				useEmbedData,
			};
		},
		computed: {
			/**
			 * Is the app currently loading?
			 * @returns {boolean} True if loading, false if not
			 */
			loading() {
				if (this.helpers.loading) {
					return true;
				}

				return false;
			},
		},
		async mounted() {
			this.lang = Site.lang_slug;

			// Set the search params from the URL
			this.useGetParams();

			// Setup the pagination data
			this.usePaginationData.postsPerPage = 10;
		},
		methods: {
			/**
			 * Hakufunktio
			 * @param {boolean} log Logataanko haku vai ei
			 */
			search(log = false) {
				this.helpers.loading = true;

				this.useQuery.log = log;
				this.useQuery.post_type = 'palvelut';

				this.useQuery.posts_per_page = -1;
				this.useQuery.taxonomies = true;

				// Reset the pagination page to 1
				this.usePaginationData.page = 1;

				const deepCopy = _.cloneDeep(this.useQuery);

				this.useCreateParams('palvelut', deepCopy);

				fetch(`${API.url}/search`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json; charset=utf-8',
						'X-WP-Nonce': API.nonce,
					},
					body: JSON.stringify(deepCopy),
				})
					.then((res) => res.json())
					.then((response) => {
						this.results.palvelut = response;
						this.allResults.palvelut = _.cloneDeep(this.results.palvelut);

						this.serviceList = response.results.filter((service) => service.no_place);
						this.termCounts = {};
						this.getTermCounts();
						this.filterByTerms(false);

						this.helpers.loading = false;
					})
					.catch((err) => {
						console.error(err);
					});
			},
			empty() {
				this.useQuery = _.cloneDeep(this.emptyQuery);

				this.search();
			},
			/**
			 * Laskee montako osumaa termeillä on
			 */
			getTermCounts() {
				const results = this.results?.palvelut?.results;
				const taxonomies = this.results.palvelut.taxonomies;

				if (taxonomies.palvelun_luokitukset) {
					const terms = taxonomies.palvelun_luokitukset;

					terms.forEach((term) => {
						this.termCounts[term.term_id] = {
							term_id: term.term_id,
							count: 0,
						};
					});

					results?.forEach((result) => {
						if (!result.taxonomies.palvelun_luokitukset) {
							return;
						}

						const resultTerms = result.taxonomies.palvelun_luokitukset;

						resultTerms.forEach((resultTerm) => {
							this.termCounts[resultTerm.term_id].count += 1;

							if (resultTerm.parent !== 0) {
								// Also increment the parent term count
								this.termCounts[resultTerm.parent].count += 1;
							}
						});
					});
				}
			},
			getMarkerTitle(element) {
				let fullTitle = '';

				const title = element[this.lang]?.title || '';
				const publisher = element.publisher_name || '';

				if (title && publisher) {
					fullTitle = `${title} (${publisher})`;
				} else if (title) {
					fullTitle = title;
				} else if (publisher) {
					fullTitle = publisher;
				}

				return fullTitle;
			},
			getMarkerIcon(element) {
				const terms = element.taxonomies.palvelun_luokitukset || [];

				const defaultIcon = {
					url: Icons.url + '/marker.png',
					height: 41,
					width: 25,
				};

				if (terms.length === 0) {
					return defaultIcon;
				}

				return terms[0].icon || defaultIcon;
			},
			getMarkerIconUrl(element) {
				const icon = this.getMarkerIcon(element);

				const { url } = icon;

				return url || Icons.url + '/marker.png';
			},
			getMarkerIconSize(element) {
				const icon = this.getMarkerIcon(element);

				const { width, height } = icon;

				return [width || 25, height || 41];
			},
			selectParentTerm(termId) {
				const termIdString = String(termId);

				const active = this.useQuery.client_filters.service_terms.includes(termIdString);

				const childBoxes = document.querySelectorAll('#term-' + termId + '-children input[type="checkbox"]');

				if (childBoxes.length > 0) {
					const values = Array.from(childBoxes).map((box) => box.value);

					if (active) {
						this.useQuery.client_filters.service_terms = Array.from(
							new Set([...this.useQuery.client_filters.service_terms, ...values]),
						);
					} else {
						this.useQuery.client_filters.service_terms = this.useQuery.client_filters.service_terms.filter(
							(term) => !values.includes(term),
						);
					}
				}

				this.filterByTerms();
			},
			filterByTerms(createParams = true) {
				const terms = this.useQuery.client_filters.service_terms;

				if (terms.length >= 1) {
					const typeCheckedTerms = terms.map((t) => Number(t));

					this.results.palvelut.results = this?.allResults?.palvelut?.results?.filter((service) => {
						const serviceTerms = service.taxonomies.palvelun_luokitukset || [];
						const serviceTermIds = serviceTerms.map((t) => Number(t.term_id));

						return typeCheckedTerms.some((item) => serviceTermIds.includes(item));
					});
				} else {
					this.results.palvelut.results = this.allResults.palvelut.results;
				}

				this.results.palvelut.count = this.results.palvelut.results.length;
				this.usePaginationData.page = 1;

				if (createParams) {
					const deepCopy = _.cloneDeep(this.useQuery);
					this.useCreateParams('palvelut', deepCopy);
				}
			},
			usePageStart,
			usePageEnd,
			useChangePage,
			usePages,
			useOpenModal,
			useCloseModal,
			useGetTitle,
			useGetDesc,
			useGetShortDesc,
			useHasShortDesc,
			useCreateParams,
			useOpenEmbed,
			useCloseEmbed,
			useCreateEmbed,
			useCopyEmbed,
			useGetParams,
		},
	};

	const app = createApp(appOptions);

	app.mount(vueSelector);
}
